// Catalog.ID Client — the Bitcash v2 ledger client
// ============================================================================
// The ledger's public contract as the member's browser reaches it
// (apps/bitcash/docs/plans/bitcash-v2.md M12, §4.13, §4.16, §4.18; apps/bitcash/worker/src/api/*.ts).
// A DIFFERENT service from the CID node: `api/client.ts` talks to `catalog.id`
// with a session token; this talks to `api.bit.cash` (`ledgerUrl` from
// `GET /api/registration/config`) with no session at all.
//
//   anonymous     GET  /api/lock/info?lockKey=      a LOCK code is an identifier (M4 reading 13)
//                 POST /api/lock/release            authorised by the Ed25519 signature in the body
//                 GET  /api/wallet/exists?ref=      existence, kind, closed; never a balance
//                 GET  /api/keys                    the ledger's signing keys (row verification)
//   holder        POST /api/address/create {fund}   X-Wallet-Key; the FUND code is generated HERE
//   (value)       POST /api/token/create {token, payee?, caps?, content?}   X-Wallet-Key; the AUTH code too
//                 POST /api/token/update {token, caps}                     X-Wallet-Key; the caps replaced WHOLE
//                 POST /api/token/revoke {token | ref}                     X-Wallet-Key
//   holder        GET  /api/wallet/info, /history?since=&limit=, /addresses, /tokens
//   (reads)                                          X-Wallet-View: the VIEW code, never the spending code (D36)
//
// ── what this wire carries ─────────────────────────────────────────────────
// Codes travel in headers or JSON bodies, never in a URL (D13). The lock lane
// sees lock keys (derived on this device from the public half of a code),
// Ed25519 public keys and signatures, and the CASH/FUND keys of the targets.
// It never sees a voucher code, an invitation code, a lookup id or a CID.
// Amounts are decimal strings of units (1 BIT = 10⁸ units, §4.21.1).
//
// The ledger never returns a FUND or AUTH code (T2): the lists answer refs. A
// caller that will need a code again keeps it itself, written down BEFORE the
// call that creates it (state/bitcash-instruments-store.ts), and a retry with
// the same code is idempotent at the ledger.
//
// ── the error contract ─────────────────────────────────────────────────────
// Every refusal carries a machine `code` beside its prose; callers walk a
// state machine over the code, never the message. A node that names no ledger
// gets `no_ledger` from every method, and no request leaves the browser.

import type { LockCondition, LockReleaseTarget } from '../crypto/bitcash';
import { toHex } from '../crypto/aes';

export type { LockCondition, LockReleaseTarget };

// ── refusal codes ───────────────────────────────────────────────────────────

/** No lock under that key (404): on a resume, "not created yet". */
export const LOCK_UNKNOWN = 'unknown';
/** Past `expiresAt`, or closed by expiry (410 since Bitcash v2 M8; matched by code). */
export const LOCK_EXPIRED = 'expired';
/** Already drained to a DIFFERENT target (409); the same target answers 200, the idempotent retry. */
export const LOCK_RELEASED = 'released';
/** Closed for another reason (409). */
export const LOCK_CLOSED = 'closed';
/** Every condition's `notAfter` has passed (403). */
export const LOCK_NO_LIVE_CONDITION = 'no_live_condition';
/** The signature verifies under no live condition (403). */
export const LOCK_BAD_SIGNATURE = 'bad_signature';
/** The target refused the credit: the lock is whole and open again (409; M4 reading 9). */
export const LOCK_RELEASE_REFUSED = 'release_refused';
/** A cash or lock target that already exists (400): a release creates its target. */
export const LOCK_TARGET_EXISTS = 'target_exists';
/** This node names no ledger (`ledgerUrl` null): nothing was sent. */
export const NO_LEDGER = 'no_ledger';

/** The machine code of a ledger refusal, or null for anything else. */
export function lockErrorCode(err: unknown): string | null {
    return err instanceof BitcashWorkerError ? err.code : null;
}

/** One refusal from the ledger. `code` is null when the body carried none (a proxy page, a 502). */
export class BitcashWorkerError extends Error {
    readonly status: number;
    readonly code: string | null;

    constructor(status: number, code: string | null, message: string) {
        super(message);
        this.name = 'BitcashWorkerError';
        this.status = status;
        this.code = code;
    }
}

// ── response shapes (narrowed to what is used) ──────────────────────────────

export interface LockReleaseResult {
    /** The credited wallet's ref, 64 lowercase hex: public, one-way (D2). */
    walletRef: string;
    /** Units moved, decimal string. */
    amount: string;
    delivery?: string;
}

export interface LockInfo {
    exists: boolean;
    /** Units, decimal string. */
    balance?: string;
    conditions?: LockCondition[];
    /** Unix ms. */
    expiresAt?: number;
    closed?: boolean;
    closedReason?: string;
    releasedTo?: string;
}

export interface AddressResult {
    r: string;
    fundRef: string;
    fee: unknown;
    delivery?: string;
}

/** A token's terms (apps/bitcash/worker/src/tokens.ts): caps in units, a fixed expiry in unix ms, decimal strings. */
export interface TokenCaps {
    day?: string;
    month?: string;
    serviceTypes?: string[];
    expiresAt?: string;
}

export interface TokenCreateRequest {
    /** The wallet's spending code: it goes in `X-Wallet-Key`. */
    walletKey: string;
    /** The client-generated AUTH code. */
    token: string;
    /** A receive address of an Operator wallet (D52); absent for an unbound token. */
    payee?: string;
    caps?: TokenCaps;
    /** The free content token (D30); M12 mints none. */
    content?: boolean;
    /** An optional site allow-list, domains (D30; kept by the ledger as tags). */
    sites?: string[];
}

/** `POST /api/token/update`: the token's own code beside the wallet code, and its caps object
 *  replaced WHOLE (apps/bitcash/worker/src/api/instruments.ts, wallet.ts) — a field left out is
 *  dropped, so a caller sends every term it means to keep. Free; the token keeps its code. */
export interface TokenUpdateRequest {
    /** The wallet's spending code: it goes in `X-Wallet-Key`. */
    walletKey: string;
    /** The token's AUTH code: the ledger takes no ref here. */
    token: string;
    caps: TokenCaps;
}

export interface TokenCreateResult {
    r: string;
    tokenRef: string;
    kind: string;
    fee: unknown;
    delivery?: string;
}

export interface WalletExists {
    exists: boolean;
    kind?: string;
    closed?: boolean;
}

export interface WalletInfo {
    ref: string;
    kind: string;
    role: string;
    country: string;
    /** Units, decimal string. */
    balance: string;
    lastSeq: string;
    era: string;
    pair?: { returnRef: string | null; termMs: string | null; fixed: boolean };
    frozen?: boolean;
    closedAt?: string | null;
    [key: string]: unknown;
}

/** A signed row as the ledger serves it: its 18 fields (§4.21.4, D59) and `sig`, base64url. */
export interface LedgerRowJson {
    version: string; network: string; era: string; kid: string; walletRef: string; seq: string; type: string;
    amount: string; balanceAfter: string; ts: string; schemeFee: string; contentPath: string; serviceType: string;
    authRef: string; counterpartyRef: string; batchId: string; country: string; payerKind: string;
    sig: string;
}

export interface WalletHistory {
    rows: LedgerRowJson[];
    more: boolean;
}

export interface LedgerAddress {
    ref: string;
    state: string;
    createdAt: string;
    unboundAt: string | null;
}

export interface LedgerToken {
    ref: string;
    kind: 'auth' | 'content' | string;
    state: string;
    payeeRef: string | null;
    caps: Record<string, unknown> | null;
    createdAt: string;
    revokedAt: string | null;
    [key: string]: unknown;
}

/** `GET /api/keys` (apps/bitcash/docs/plans/bitcash-v2.md §4.21.10) as the ledger serves it: its eras are JSON
 *  NUMBERS (the Worker's keysDocument), where a row's `era` is a string like all its fields. */
export interface LedgerKeys {
    net: string;
    era: number;
    current: string;
    keys: Array<{ kid: string; publicKey: string; era: number; validFrom: string; validTo: string | null; certificateRef: string | null }>;
}

export interface LockReleaseRequest {
    /** The lock being drained, compact or display form. */
    lockKey: string;
    target: LockReleaseTarget;
    /** 64 raw bytes, or the 128 lowercase hex characters they encode. */
    signature: Uint8Array | string;
    conditionIndex?: number;
}

// ── the client ──────────────────────────────────────────────────────────────

export interface BitcashWorkerOptions {
    /** The ledger's origin; a function is resolved at CALL time. Null: no ledger. */
    baseUrl: string | null | (() => string | null);
    /** Injected so no unit test can reach the network. */
    fetch?: typeof fetch;
}

export interface BitcashWorker {
    /** Drain a lock into a fresh wallet, an address, or another lock. IDEMPOTENT to the same target. */
    releaseLock(req: LockReleaseRequest): Promise<LockReleaseResult>;
    /** Read a lock; a key that names none answers `{exists: false}`. */
    lockInfo(lockKey: string): Promise<LockInfo>;
    /** Bind the client-generated receive address `fund` to the wallet. The first is free. */
    createReceiveAddress(walletKey: string, fund: string): Promise<AddressResult>;
    /** Create the client-generated token. The first non-content token of a wallet is free. */
    createToken(req: TokenCreateRequest): Promise<TokenCreateResult>;
    /** Replace a token's terms; the token keeps its code. */
    updateToken(req: TokenUpdateRequest): Promise<{ token: unknown }>;
    revokeToken(walletKey: string, which: { token: string } | { ref: string }): Promise<{ r: string; tokenRef: string }>;
    walletExists(ref: string): Promise<WalletExists>;
    walletInfo(viewCode: string): Promise<WalletInfo>;
    walletHistory(viewCode: string, since?: string, limit?: number): Promise<WalletHistory>;
    walletAddresses(viewCode: string): Promise<{ addresses: LedgerAddress[] }>;
    walletTokens(viewCode: string): Promise<{ tokens: LedgerToken[] }>;
    keys(): Promise<LedgerKeys>;
}

const HEX128 = /^[0-9a-f]{128}$/;

/** The 128 lowercase hex characters the wire wants, checked BEFORE the network. */
function signatureHex(signature: Uint8Array | string): string {
    if (signature instanceof Uint8Array) {
        if (signature.length !== 64) throw new Error('A lock release signature is 64 bytes');
        return toHex(signature);
    }
    if (typeof signature === 'string' && HEX128.test(signature)) return signature;
    throw new Error('A lock release signature is 128 lowercase hex characters');
}

export function createBitcashWorker(options: BitcashWorkerOptions): BitcashWorker {
    const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

    function base(): string {
        const raw = typeof options.baseUrl === 'function' ? options.baseUrl() : options.baseUrl;
        if (!raw) throw new BitcashWorkerError(0, NO_LEDGER, 'This Catalog.ID server is not connected to a Bitcash ledger.');
        return raw.replace(/\/+$/, '');
    }

    async function call<T>(path: string, init?: RequestInit): Promise<T> {
        const resp = await doFetch(`${base()}${path}`, init);
        // Read the body once, as text: a refusal may be JSON (the ledger's) or a proxy's HTML page.
        const text = await resp.text();
        let parsed: unknown = undefined;
        try {
            parsed = text ? JSON.parse(text) : undefined;
        } catch { /* not JSON — handled below */ }
        if (!resp.ok) {
            const body = (parsed ?? {}) as { error?: unknown; code?: unknown };
            const code = typeof body.code === 'string' ? body.code : null;
            const message = typeof body.error === 'string' && body.error ? body.error : `ledger call failed (${resp.status})`;
            throw new BitcashWorkerError(resp.status, code, message);
        }
        return parsed as T;
    }

    function post<T>(path: string, body: unknown, walletKey?: string): Promise<T> {
        return call<T>(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(walletKey !== undefined ? { 'X-Wallet-Key': walletKey } : {}) },
            body: JSON.stringify(body),
        });
    }

    function read<T>(path: string, viewCode: string): Promise<T> {
        return call<T>(path, { method: 'GET', headers: { 'X-Wallet-View': viewCode } });
    }

    return {
        // `async`, deliberately: `signatureHex` throws, and a Promise-typed method must REJECT.
        async releaseLock(req: LockReleaseRequest): Promise<LockReleaseResult> {
            const signature = signatureHex(req.signature);
            return post<LockReleaseResult>('/api/lock/release', {
                lockKey: req.lockKey,
                target: req.target,
                signature,
                ...(req.conditionIndex !== undefined ? { conditionIndex: req.conditionIndex } : {}),
            });
        },

        async lockInfo(lockKey: string): Promise<LockInfo> {
            return call<LockInfo>(`/api/lock/info?lockKey=${encodeURIComponent(lockKey)}`);
        },

        async createReceiveAddress(walletKey: string, fund: string): Promise<AddressResult> {
            return post<AddressResult>('/api/address/create', { fund }, walletKey);
        },

        async createToken(req: TokenCreateRequest): Promise<TokenCreateResult> {
            return post<TokenCreateResult>('/api/token/create', {
                token: req.token,
                ...(req.content ? { content: true } : {}),
                ...(req.payee !== undefined ? { payee: req.payee } : {}),
                ...(req.caps !== undefined ? { caps: req.caps } : {}),
                ...(req.sites !== undefined && req.sites.length > 0 ? { sites: req.sites } : {}),
            }, req.walletKey);
        },

        async updateToken(req: TokenUpdateRequest): Promise<{ token: unknown }> {
            return post<{ token: unknown }>('/api/token/update', { token: req.token, caps: req.caps }, req.walletKey);
        },

        async revokeToken(walletKey: string, which: { token: string } | { ref: string }): Promise<{ r: string; tokenRef: string }> {
            return post<{ r: string; tokenRef: string }>('/api/token/revoke', which, walletKey);
        },

        async walletExists(ref: string): Promise<WalletExists> {
            return call<WalletExists>(`/api/wallet/exists?ref=${encodeURIComponent(ref)}`);
        },

        async walletInfo(viewCode: string): Promise<WalletInfo> {
            return read<WalletInfo>('/api/wallet/info', viewCode);
        },

        async walletHistory(viewCode: string, since = '0', limit?: number): Promise<WalletHistory> {
            const query = `since=${encodeURIComponent(since)}${limit !== undefined ? `&limit=${limit}` : ''}`;
            return read<WalletHistory>(`/api/wallet/history?${query}`, viewCode);
        },

        async walletAddresses(viewCode: string): Promise<{ addresses: LedgerAddress[] }> {
            return read<{ addresses: LedgerAddress[] }>('/api/wallet/addresses', viewCode);
        },

        async walletTokens(viewCode: string): Promise<{ tokens: LedgerToken[] }> {
            return read<{ tokens: LedgerToken[] }>('/api/wallet/tokens', viewCode);
        },

        async keys(): Promise<LedgerKeys> {
            return call<LedgerKeys>('/api/keys');
        },
    };
}
